import type { AddAgentRequest, AddAgentResponse, AgentApiResponse, ResponseDeleteAgent, SyncOutlookContactsRequest, SyncOutlookContactsResponse } from '@frontend/Models/Agent/AgentRequestResponseModels';
import type { Agent } from '@frontend/Models/Agent/AgentModels';
import HttpService from './HttpService';
import {
  pathAgentId,
  pathAgents,
  pathAgentsSyncOutlook,
} from '@frontend/__settings__/septSoftwarePaths';

// create a shared HttpService instance; HttpService will use the env base URL by default
const http = new HttpService();

class AgentsService {
  /**
   * This method deletes an agent by ID.
   * @param agentId the ID of the agent to delete
   * @returns 
   */
  static async deleteAgent(agentId: string): Promise<ResponseDeleteAgent> {
    const response = await http.delete<AgentApiResponse<ResponseDeleteAgent>>(pathAgentId(agentId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while deleting the agent.');
    }
    return response.data;
  }

  /**
   * This gets a list of all agents.
   * @returns 
   */
  static async getAgents(): Promise<Agent[]> {
    const response = await http.get<AgentApiResponse<Agent[]>>(`/${pathAgents}`);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching agents.');
    }
    return response.data;
  }

  /**
   * This method gets a single agent by ID.
   * @param agentId the ID of the agent to retrieve
   * @returns 
   */
  static async getAgent(agentId: string): Promise<Agent> {
    const response = await http.get<AgentApiResponse<Agent>>(pathAgentId(agentId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching the agent.');
    }
    return response.data;
  }

  /**
   * This method adds a new agent.
   * @param agentData the data of the agent to add
   * @returns true if the agent was added successfully, false otherwise
   */
  static async addAgent(agentData: AddAgentRequest): Promise<boolean> {
    const response = await http.post<AgentApiResponse<AddAgentResponse>>(`/${pathAgents}`, agentData);
    return response.successful === true && response.data?.message === 'Agent added successfully.';
  }

  /**
   * This method syncs Outlook contacts.
   * @param contactsData the contacts data to sync
   * @returns the sync result with counts and messages
   */
  static async syncOutlookContacts(contactsData: SyncOutlookContactsRequest): Promise<SyncOutlookContactsResponse> {
    const response = await http.post<AgentApiResponse<SyncOutlookContactsResponse>>(pathAgentsSyncOutlook, contactsData);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while syncing contacts.');
    }
    return response.data;
  }

  /**
   * This method updates an existing agent's information.
   * @param agentId the ID of the agent to update
   * @param agentData the updated agent data
   * @returns true if the agent was updated successfully, false otherwise
   */
  static async updateAgent(agentId: string, agentData: Partial<AddAgentRequest>): Promise<boolean> {
    const response = await http.put<AgentApiResponse<AddAgentResponse>>(pathAgentId(agentId), agentData);
    return response.successful === true && response.data?.message === 'Agent updated successfully.';
  }
}

export default AgentsService;
